import { formatDateOnly } from "@/lib/datetime"
import { contactStateOf, visitStateOf } from "./clock"
import type {
  TcmBridge,
  TcmBridgeOwner,
  TcmBridgeStatus,
  TcmCarePlan,
  TcmEpisode,
  TcmNeedKey,
  TcmResourceCategory,
  TcmRiskTier,
} from "../types"

/**
 * Words for the care plan's codes, and the case's 30 days as one journey (discharge to day 30).
 * graham's care plan carries library keys, not text; the portal names them here.
 */

export const NEED_LABELS: Record<TcmNeedKey, string> = {
  HOME_HEALTH: "Home health",
  DME: "Equipment (DME)",
  MEDS_ACCESS: "Medicines picked up",
  LABS: "Labs",
  FOLLOW_UP: "Specialist follow-up",
  PENDING_RESULTS: "Pending results",
  TRANSPORT: "Rides",
  CAREGIVER: "Caregiver help",
  FOOD: "Food",
  SAFETY: "Home safety",
  OTHER: "Other",
}

export const RESOURCE_LABELS: Record<TcmResourceCategory, string> = {
  ...NEED_LABELS,
  CLINIC_LINE: "Clinic line (printed on the plan)",
  AFTER_HOURS: "After-hours line",
}

export const BRIDGE_STATUS: Record<TcmBridgeStatus, { label: string; variant: "warning" | "info" | "success" | "muted" }> = {
  PROPOSED: { label: "Suggested", variant: "warning" },
  NEEDED: { label: "Needed", variant: "warning" },
  ARRANGED: { label: "Arranged", variant: "info" },
  CONFIRMED: { label: "Patient confirmed", variant: "success" },
  NOT_NEEDED: { label: "Not needed", variant: "muted" },
  DECLINED: { label: "Patient declined", variant: "muted" },
}

export const OWNER_LABELS: Record<TcmBridgeOwner, string> = {
  CLINIC: "Clinic",
  PEAR: "Pear",
  PATIENT: "Patient",
  CAREGIVER: "Caregiver",
  VENDOR: "Vendor",
  HOSPITAL: "Hospital",
}

/** A need someone still has to act on. */
export function isOpenBridge(b: TcmBridge): boolean {
  return b.status === "PROPOSED" || b.status === "NEEDED"
}

export const RISK_ITEM_LABELS: Record<string, string> = {
  hgb_discharge: "Hemoglobin under 12 at discharge",
  oncology_service: "Discharged from oncology",
  sodium_discharge: "Sodium under 135 at discharge",
  procedure_during_stay: "Procedure during the stay",
  admission_type: "Urgent or emergency admission",
  admissions_prior_12m: "Hospital stays in the prior 12 months",
  los_days: "Length of stay",
  charlson: "Charlson comorbidity score",
  ed_visits_prior_6m: "ED visits in the prior 6 months",
}

/** "lives_alone" -> "Lives alone". */
export function humanize(key: string): string {
  const words = key.replace(/[._]+/g, " ").trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export const TIER_LABEL: Record<TcmRiskTier, string> = {
  HIGH: "High",
  INTERMEDIATE: "Medium",
  LOW: "Low",
  NA: "Not scored",
}

export const LANG_LABEL = { en: "English", es: "Español", vi: "Tiếng Việt" } as const

export type StepState = "done" | "current" | "overdue" | "upcoming" | "na"

export interface JourneyStep {
  key: "discharge" | "contact" | "plan" | "visit" | "calls" | "bill" | "day30"
  title: string
  state: StepState
  detail: string
}

const md = (d: string | null | undefined) => formatDateOnly(d ?? null, "EEE M/d")

const CALL_DONE = new Set(["COMPLETED", "UNREACHED", "SKIPPED", "CANCELLED"])

/**
 * The case's 30 days as steps: discharge, the 2-day contact, the plan and needs at home, the visit,
 * Pear's weekly calls, billing and day 30. Each is done, current, overdue, upcoming or n/a.
 */
export function journey(ep: TcmEpisode, plan: TcmCarePlan | null | undefined, today: string): JourneyStep[] {
  const c = ep.clock
  const closed = ep.stage.code === "CLOSED" || ep.stage.code === "BILLED"
  const isTcm = ep.kind === "TCM"

  const discharge: JourneyStep = ep.discharge_date
    ? { key: "discharge", title: "Discharge", state: "done", detail: md(ep.post_acute_discharge_date ?? ep.discharge_date) }
    : { key: "discharge", title: "Discharge", state: "current",
        detail: ep.stage.code === "RECORDS_NEEDED" ? "Confirm the stay and the date" : "Still in the hospital" }

  let contact: JourneyStep
  if (!isTcm) contact = { key: "contact", title: "2-day contact", state: "na", detail: "Not needed for an ED visit" }
  else if (!c) contact = { key: "contact", title: "2-day contact", state: "upcoming", detail: "Due 2 business days after discharge" }
  else {
    const s = contactStateOf(ep, today)
    contact = {
      key: "contact",
      title: "2-day contact",
      state: s === "reached" || s === "attempted" ? "done" : s === "late" || s === "missed" ? "overdue" : "current",
      detail: {
        reached: `Reached ${md(ep.contact_date)}`,
        attempted: "2 attempts logged",
        late: "Made after the deadline",
        missed: `Missed (due ${md(c.contact_due)})`,
        due: `Due ${md(c.contact_due)}`,
      }[s],
    }
  }

  const open = plan ? plan.bridges.filter(isOpenBridge).length : 0
  const planStep: JourneyStep = !plan
    ? { key: "plan", title: "Plan & needs", state: c ? "current" : "upcoming", detail: "Pear is preparing the plan" }
    : { key: "plan", title: "Plan & needs", state: open ? "current" : "done",
        detail: open ? `${open} need${open === 1 ? "" : "s"} to arrange` : "Needs at home in place" }

  let visit: JourneyStep
  const vs = c ? visitStateOf(ep, today) : "none"
  const window = isTcm ? "by day 7 or 14" : "soon after the ED visit"
  if (vs === "done") visit = { key: "visit", title: "Visit", state: "done", detail: `Seen ${md(ep.f2f_completed_date)}` }
  else if (vs === "passed") visit = { key: "visit", title: "Visit", state: "overdue", detail: `Was ${md(ep.f2f_scheduled_date)}; mark it done or rebook` }
  else if (vs === "booked" || vs === "today") visit = { key: "visit", title: "Visit", state: "current", detail: vs === "today" ? "Today" : `Booked ${md(ep.f2f_scheduled_date)}` }
  else if (isTcm && c && c.day > 14) visit = { key: "visit", title: "Visit", state: "overdue", detail: "Past day 14: not TCM-billable" }
  else visit = { key: "visit", title: "Visit", state: c ? "current" : "upcoming", detail: `Book it ${window}` }

  const checkins = plan?.checkins ?? []
  const calls: JourneyStep = !checkins.length
    ? { key: "calls", title: "Weekly calls", state: "na", detail: "Not scheduled" }
    : checkins.every((k) => CALL_DONE.has(k.status))
      ? { key: "calls", title: "Weekly calls", state: "done",
          detail: `${checkins.filter((k) => k.status === "COMPLETED").length} of ${checkins.length} reached` }
      : { key: "calls", title: "Weekly calls", state: "current",
          detail: (() => {
            const next = checkins.find((k) => !CALL_DONE.has(k.status))!
            return `Week ${next.week}: ${md(next.window_start)}–${md(next.window_end)}`
          })() }

  let bill: JourneyStep
  if (!isTcm) bill = { key: "bill", title: "Bill", state: "na", detail: "No TCM claim" }
  else if (ep.billed_code) bill = { key: "bill", title: "Bill", state: "done", detail: `${ep.billed_code} on ${md(ep.billed_date)}` }
  else if (ep.stage.code === "READY_TO_BILL") bill = { key: "bill", title: "Bill", state: "current", detail: ep.stage.next_action }
  else if (closed) bill = { key: "bill", title: "Bill", state: "na", detail: "Closed without a claim" }
  else bill = { key: "bill", title: "Bill", state: "upcoming", detail: "After the visit" }

  const day30: JourneyStep = !c
    ? { key: "day30", title: "Day 30", state: closed ? "done" : "upcoming", detail: closed ? "Closed" : "30 days from discharge" }
    : c.day >= 29 || closed
      ? { key: "day30", title: "Day 30", state: "done", detail: closed ? "Closed" : `Ended ${md(c.period_end)}` }
      : { key: "day30", title: "Day 30", state: "upcoming", detail: `${md(c.period_end)} · hand back to the clinic's usual care` }

  return [discharge, contact, planStep, visit, calls, bill, day30]
}

const SLOT_LABELS: Record<string, string> = {
  clinic: "Clinic name",
  clinic_phone: "Clinic phone number",
  after_hours_phone: "After-hours number",
  pear_phone: "Pear's number",
  date: "A date",
  by_date: "A due date",
  dates: "Dates",
  time: "A time",
  provider: "A provider's name",
  specialty: "The specialty",
  facility: "The facility",
  limit: "A limit to watch for",
  week: "The week",
}

/** "careplan_general.call_clinic:after_hours_phone" -> "After-hours number (call clinic)". */
export function blankLabel(blank: string): string {
  const [key, slot] = blank.split(":")
  const section = humanize(key.split(".").pop() ?? key).toLowerCase()
  return slot ? `${SLOT_LABELS[slot] ?? humanize(slot)} (${section})` : humanize(section)
}
